import { type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTeam, useDeleteTeam, useTeamChildren, useTeamDescendants } from "@/hooks/queries/use-teams";
import { useEpicRequestsPaged } from "@/hooks/queries/use-epic-requests";
import { useEpicsPaged } from "@/hooks/queries/use-epics";
import { useAuthStore } from "@/stores/auth-store";
import { MemberList } from "@/components/teams/member-list";
import { TeamCard } from "@/components/teams/team-card";
import { TeamBreadcrumb } from "@/components/teams/team-breadcrumb";
import { TeamTreeSidebar } from "@/components/teams/team-tree-sidebar";
import { EpicForm } from "@/components/epics/epic-form";
import { NewRequestButton } from "@/components/epic-requests/new-request-button";
import { usePageTitle } from "@/hooks/use-page-title";
import { TeamForm } from "@/components/teams/team-form";
import { AddMemberModal } from "@/components/teams/add-member-modal";
import { StatusList } from "@/components/teams/status-list";
import { StatusForm } from "@/components/teams/status-form";
import { ProjectRepositoryCard } from "@/components/teams/project-repository-card";
import { MergePolicyCard } from "@/components/teams/merge-policy-card";
import { FastModeCard } from "@/components/teams/fast-mode-card";
import { AutoApplyMigrationsCard } from "@/components/teams/auto-apply-migrations-card";
import { CheckModeCard } from "@/components/teams/check-mode-card";
import { ComplianceThresholdCard } from "@/components/teams/compliance-threshold-card";
import { ProjectFinishGitHubSetup } from "@/components/github/finish-github-setup";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { EntityCard } from "@/components/ui/entity-card";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ExternalLink,
  FolderTree,
  Lightbulb,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  UserPlus,
} from "lucide-react";
import type { EpicRequestWithReactionCounts } from "@/lib/api/epic-requests";
import type { Status } from "@/lib/api/types";
import { getTeamLevelBadgeClass, getTeamLevelLabel } from "@/lib/team-hierarchy";
import { getTeamHueColor } from "@/lib/team-hue";
import { useIdeaHref } from "@/hooks/use-new-look";

function TeamStatCard({ label, value, description }: { label: string; value: number; description: string }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-foreground">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function DisabledTeamAction({ reason, children }: { reason: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-not-allowed">{children}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{reason}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function EpicRequestCard({ request }: { request: EpicRequestWithReactionCounts }) {
  const ideaHref = useIdeaHref();
  const hueColor = request.status === "approved" ? "#22C55E" : request.status === "pending" ? "#EAB308" : request.status === "planning" ? "#8B5CF6" : request.status === "converted" ? "#3B82F6" : "#EF4444";

  return (
    <EntityCard
      href={ideaHref(request.id)}
      hueColor={hueColor}
      icon={<Lightbulb className="h-5 w-5" style={{ color: hueColor }} />}
      title={request.identifier ? `${request.identifier} — ${request.title}` : request.title}
      subtitle={request.description}
      meta={request.status}
      secondaryMeta={request.team?.key ?? undefined}
      footer={
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{request.requestedBy?.name ?? "Unknown requester"}</span>
          <span className="inline-flex items-center gap-1 text-foreground">
            Open
            <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      }
    />
  );
}

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: team, isLoading } = useTeam(teamId ?? "");
  const { data: childTeams } = useTeamChildren(teamId ?? "");
  const { data: descendants } = useTeamDescendants(teamId ?? "");
  const { data: epicsData } = useEpicsPaged(teamId ? { teamId, limit: 12, state: "active" } : {});
  const { data: requestsData } = useEpicRequestsPaged({ limit: 100, status: "approved" });
  usePageTitle(team?.name ?? "Team");
  const deleteTeam = useDeleteTeam();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEpicFormOpen, setIsEpicFormOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [isStatusFormOpen, setIsStatusFormOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "settings" ? "settings" : "members");

  const teamRequests = useMemo(
    () => (requestsData?.data ?? []).filter((request) => request.teamId === team?.id),
    [requestsData?.data, team?.id],
  );
  const directChildren = childTeams ?? [];
  const allDescendants = descendants ?? [];
  const leafDescendantCount = allDescendants.filter((descendant) => descendant.canOwnEpics).length;
  const activeEpics = epicsData?.data ?? [];
  const createDisabledReason = `Only discipline teams without non-archived child teams can own epics and requests. ${team?.name ?? "This team"} cannot own epics right now.`;

  if (isLoading) {
    return (
      <div className="pt-4">
        <div className="grid gap-4 lg:grid-cols-[320px,minmax(0,1fr)]">
          <div className="hidden rounded-2xl bg-muted/60 lg:block lg:h-[600px]" />
          <div className="space-y-4">
            <div className="h-40 animate-pulse rounded-2xl bg-muted/60" />
            <div className="h-80 animate-pulse rounded-2xl bg-muted/60" />
          </div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="pt-4">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Team not found</p>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    await deleteTeam.mutateAsync(team.id);
    navigate("/teams");
  };

  const handleEditStatus = (status: Status) => {
    setEditingStatus(status);
    setIsStatusFormOpen(true);
  };

  const handleAddStatus = () => {
    setEditingStatus(undefined);
    setIsStatusFormOpen(true);
  };

  const hueColor = getTeamHueColor(team.name);

  return (
    <div className="relative">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${hueColor}2B, transparent 70%)`,
          backgroundColor: `${hueColor}09`,
          animation: "glow-enter 0.5s ease-out forwards",
        }}
      />

      <div className="relative z-10 pt-4">
        <div className="grid gap-4 lg:grid-cols-[320px,minmax(0,1fr)]">
          <div className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
            <TeamTreeSidebar currentTeamId={team.id} />
          </div>

          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-4 md:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <TeamBreadcrumb teamId={team.id} />
                {isMobile ? (
                  <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm">
                        <FolderTree className="mr-2 h-4 w-4" />
                        Hierarchy
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="border-r p-0">
                      <SheetHeader className="border-b border-white/[0.08]">
                        <SheetTitle>Team hierarchy</SheetTitle>
                      </SheetHeader>
                      <div className="p-4">
                        <TeamTreeSidebar currentTeamId={team.id} onNavigate={() => setIsSidebarOpen(false)} />
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : null}
              </div>

              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-semibold text-foreground">{team.name}</h1>
                    <Badge variant="outline" className={`border px-2 py-0 text-[10px] font-medium ${getTeamLevelBadgeClass(team.level)}`}>
                      {getTeamLevelLabel(team.level)}
                    </Badge>
                    {team.canOwnEpics ? (
                      <Badge variant="outline" className="border-emerald-500/20 bg-emerald-500/10 text-emerald-200">
                        <Sparkles className="mr-1 h-3 w-3" />
                        Epic owner
                      </Badge>
                    ) : null}
                  </div>
                  <p className="max-w-3xl text-sm text-muted-foreground">
                    {team.canOwnEpics
                      ? "This discipline can own epics and Epic Requests. Use the actions on the right to start new work or review active items below."
                      : "This parent team organizes child teams beneath it. Browse child cards, descendant counts, and the hierarchy-aware navigation to drill deeper."}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={() => { setIsEditOpen(true); }}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button variant="outline" onClick={() => { setShowDeleteDialog(true); }}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                  {team.canOwnEpics ? (
                    <Button onClick={() => { setIsEpicFormOpen(true); }}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Epic
                    </Button>
                  ) : (
                    <DisabledTeamAction reason={createDisabledReason}>
                      <Button disabled>
                        <Plus className="mr-2 h-4 w-4" />
                        Create Epic
                      </Button>
                    </DisabledTeamAction>
                  )}
                  {team.canOwnEpics ? (
                    <NewRequestButton>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Epic Request
                    </NewRequestButton>
                  ) : (
                    <DisabledTeamAction reason={createDisabledReason}>
                      <Button disabled>
                        <Plus className="mr-2 h-4 w-4" />
                        Create Epic Request
                      </Button>
                    </DisabledTeamAction>
                  )}
                </div>
              </div>
            </div>

            <div className="glass-card rounded-2xl p-4 md:p-6">
              {team.canOwnEpics ? (
                <div className="space-y-6">
                  <div className="grid gap-3 md:grid-cols-3">
                    <TeamStatCard label="Active epics" value={activeEpics.length} description="Open work owned by this discipline" />
                    <TeamStatCard label="Approved requests" value={teamRequests.length} description="Approved requests waiting or already planning" />
                    <TeamStatCard label="Members" value={team._count?.memberships ?? 0} description="People directly assigned to this team" />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold">Owned epics</h2>
                        <p className="text-sm text-muted-foreground">Active epics scoped to this discipline.</p>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/epics?teamId=${team.id}`}>See all epics</Link>
                      </Button>
                    </div>
                    {activeEpics.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
                        No active epics yet for this discipline.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {activeEpics.slice(0, 6).map((epic) => (
                          <EntityCard
                            key={epic.id}
                            href={`/epics/${epic.id}`}
                            hueColor={getTeamHueColor(epic.team?.name ?? team.name)}
                            icon={<FolderTree className="h-5 w-5" style={{ color: getTeamHueColor(epic.team?.name ?? team.name) }} />}
                            title={epic.identifier ? `${epic.identifier} — ${epic.name}` : epic.name}
                            subtitle={epic.description}
                            meta={epic.currentFeatureIdentifier ?? epic.team?.key ?? team.key}
                            secondaryMeta={epic._count?.features ? `${String(epic._count.features)} features` : undefined}
                            footer={<span className="text-xs text-muted-foreground">Updated {new Date(epic.updatedAt).toLocaleDateString()}</span>}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold">Epic requests</h2>
                      <p className="text-sm text-muted-foreground">Approved requests already associated with this discipline.</p>
                    </div>
                    {teamRequests.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
                        No approved Epic Requests linked to this discipline yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {teamRequests.slice(0, 6).map((request) => (
                          <EpicRequestCard key={request.id} request={request} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid gap-3 md:grid-cols-3">
                    <TeamStatCard label="Direct children" value={directChildren.length} description="Immediate teams nested beneath this one" />
                    <TeamStatCard label="Descendants" value={allDescendants.length} description="Total teams below this point in the tree" />
                    <TeamStatCard label="Epic owner teams" value={leafDescendantCount} description="Leaf disciplines available lower in the hierarchy" />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold">Child teams</h2>
                      <p className="text-sm text-muted-foreground">Navigate deeper into the hierarchy by opening one of the child teams below.</p>
                    </div>
                    {directChildren.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
                        No child teams are linked here yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {directChildren.map((childTeam) => (
                          <TeamCard key={childTeam.id} team={childTeam} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* The Settings tab already shows the repository card's Connect GitHub step. */}
            {team.canOwnEpics && activeTab !== "settings" ? <ProjectFinishGitHubSetup teamId={team.id} /> : null}

            <div className="glass-card rounded-2xl p-4 md:p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4">
                  <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <h2 className="text-lg font-medium">Team Members</h2>
                    <Button variant="outline" size="sm" onClick={() => setShowAddMember(true)} className="w-full md:w-auto">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Add Member
                    </Button>
                  </div>
                  <MemberList teamId={team.id} currentUserId={user?.id ?? ""} />
                </TabsContent>

                <TabsContent value="settings" className="mt-4">
                  <div className="space-y-6">
                    {team.canOwnEpics ? <ProjectRepositoryCard team={team} /> : null}
                    {team.canOwnEpics ? <MergePolicyCard team={team} /> : null}
                    {team.canOwnEpics ? <FastModeCard team={team} /> : null}
                    {team.canOwnEpics ? <AutoApplyMigrationsCard team={team} /> : null}
                    {team.canOwnEpics ? <CheckModeCard team={team} /> : null}
                    {team.canOwnEpics ? <ComplianceThresholdCard team={team} /> : null}
                    <div>
                      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-medium">Workflow Statuses</h3>
                          <p className="text-sm text-muted-foreground">
                            Configure the statuses available for features and tasks.
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleAddStatus} className="w-full md:w-auto">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Status
                        </Button>
                      </div>
                      <StatusList teamId={team.id} onEdit={handleEditStatus} />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <TeamForm team={team} open={isEditOpen} onOpenChange={setIsEditOpen} />
            <EpicForm open={isEpicFormOpen} onOpenChange={setIsEpicFormOpen} defaultTeamId={team.id} />

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Team</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete "{team.name}"? This will remove all
                    members and archive all epics in this team. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => void handleDelete()}
                    disabled={deleteTeam.isPending}
                  >
                    {deleteTeam.isPending ? "Deleting..." : "Delete"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <AddMemberModal
              teamId={team.id}
              open={showAddMember}
              onOpenChange={setShowAddMember}
            />

            <StatusForm
              teamId={team.id}
              status={editingStatus}
              open={isStatusFormOpen}
              onOpenChange={setIsStatusFormOpen}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
